import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@roberttamas/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CopyButton } from "@/components/copy-button";
import { Search } from "@/components/icons";
import { EmptyOrdersIllustration } from "@/components/illustrations";
import { OrderPackageTags } from "@/components/order-package-tags";
import i18n from "@/i18n";
import { listOrders } from "@/lib/api";
import { formatDate as formatLocaleDate } from "@/lib/locale-format";
import { formatAmount } from "@/lib/pricing";
import type { AdminOrderEntry, ListOrdersResp } from "@/lib/types";

const COLUMN_COUNT = 8;
const DEBOUNCE_MS = 300;

// Rendered from a component that subscribes via useTranslation.
function statusBadge(order: AdminOrderEntry) {
  if (order.status === "paid") {
    return (
      <Badge className="bg-green-600 text-white hover:bg-green-600">
        {i18n.t("orders.statusPaid")}
      </Badge>
    );
  }
  if (order.status === "cancelled") {
    return <Badge variant="outline">{i18n.t("orders.statusCancelled")}</Badge>;
  }
  const expired =
    typeof order.payment?.dueDate === "string" &&
    new Date(order.payment.dueDate).getTime() < Date.now();
  return expired ? (
    <Badge variant="outline" className="text-muted-foreground">{i18n.t("orders.statusExpired")}</Badge>
  ) : (
    <Badge variant="outline">{i18n.t("orders.statusAwaiting")}</Badge>
  );
}

function formatDate(iso: string | null): string {
  return iso ? formatLocaleDate(new Date(iso)) : "-";
}

/** First 10 chars of an id — enough to recognize/paste-match, short enough to fit a table cell. */
function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 10)}…` : id;
}

export function OrdersListPage() {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery<ListOrdersResp>({
    queryKey: ["orders", search],
    queryFn: () => listOrders({ search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const orders = query.data?.orders ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("orders.eyebrow")}
        title={t("orders.title")}
        subtitle={t("orders.subtitle")}
      />

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("orders.searchPlaceholder")}
          aria-label={t("orders.searchAria")}
          className="pl-9"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("orders.colOrder")}</TableHead>
                <TableHead>{t("orders.colBuyer")}</TableHead>
                <TableHead>{t("orders.colPackage")}</TableHead>
                <TableHead className="text-right">{t("orders.colAmount")}</TableHead>
                <TableHead>{t("orders.colVs")}</TableHead>
                <TableHead>{t("orders.colStatus")}</TableHead>
                <TableHead>{t("orders.colCreated")}</TableHead>
                <TableHead>{t("orders.colPaid")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {query.isError && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="flex items-center justify-between gap-4 py-6 text-sm">
                      <span className="text-destructive">{t("orders.loadFailed")}</span>
                      <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                        {t("common.retry")}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && orders.length === 0 && !search && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="flex flex-col items-center gap-3 py-12 text-center">
                      <EmptyOrdersIllustration className="h-28 w-28" />
                      <p className="text-sm text-muted-foreground">
                        {t("orders.empty")}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {query.data && orders.length === 0 && search && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <div className="py-10 text-center text-sm text-muted-foreground">
                      {t("orders.noMatch", { query: search })}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {orders.map((order) => (
                <TableRow key={order.orderId}>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {shortId(order.orderId)}
                      </code>
                      <CopyButton value={order.orderId} label={t("orders.copyOrderId")} />
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[14rem] truncate" title={order.email}>
                    {order.email}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="font-medium">{order.snapshot.tierName}</span>
                      <OrderPackageTags snapshot={order.snapshot} />
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-right font-mono">
                    {formatAmount(order.snapshot.amountMinor, order.snapshot.currency)}
                  </TableCell>
                  <TableCell className="font-mono">
                    {order.payment?.variableSymbol ?? "-"}
                  </TableCell>
                  <TableCell>
                    {statusBadge(order)}
                    {order.status === "paid" && order.paidVia === "manual" && (
                      <span className="ml-1 text-xs text-muted-foreground">{t("orders.manualTag")}</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(order.createdAt)}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(order.paidAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
